import type { VerificationRejectionReasonCode } from "@wallyo/contracts/console/verifications";

import { reasonProblem, type DecisionKind } from "./decision";

/**
 * The approve or reject dialog. Never optimistic: `pending` until the server
 * answers, and a failure keeps what was typed (and, in the screen, the same
 * Idempotency-Key) so "try again" can't do it twice.
 */
export type DialogState = {
  kind: DecisionKind;
  /** A rejection's reason code: what the pro is told, in their language. */
  reasonCode: VerificationRejectionReasonCode | null;
  /** An approval's reason, or a rejection's note for the team. */
  reason: string;
  pending: boolean;
  problem: string | null;
  error: unknown;
};

/** What the reviewer does in the dialog. */
export type DialogEvent =
  | { type: "open"; kind: DecisionKind }
  | { type: "reasonCode"; reasonCode: VerificationRejectionReasonCode | null }
  | { type: "reason"; reason: string }
  | { type: "submit" }
  | { type: "close" };

/** What the screen adds once the request is on its way or answered. */
export type DialogResult = { type: "failed"; error: unknown } | { type: "succeeded" };

export function dialogReducer(state: DialogState | null, event: DialogEvent | DialogResult): DialogState | null {
  switch (event.type) {
    case "open":
      return state ?? { kind: event.kind, reasonCode: null, reason: "", pending: false, problem: null, error: null };
    case "reasonCode":
      if (!state || state.pending) return state;
      return { ...state, reasonCode: event.reasonCode, problem: null, error: null };
    case "reason":
      if (!state || state.pending) return state;
      return { ...state, reason: event.reason, problem: null, error: null };
    case "submit": {
      if (!state || state.pending) return state;
      const problem = reasonProblem(state.kind, state.reason, state.reasonCode);
      return problem ? { ...state, problem } : { ...state, pending: true, problem: null, error: null };
    }
    case "failed":
      return state ? { ...state, pending: false, error: event.error } : state;
    case "succeeded":
      return null;
    case "close":
      return state?.pending ? state : null;
  }
}
